/**
 * Routing by hash: #/ = project list, #/p/<id> = editor, #/account, #/library, #/voiceover, #/voices, #/connect/<request>
 * (approve Claude's connector). Signed out: the login, plus the
 * emailed-link pages #/invite/<token>, #/reset/<token> and #/forgot.
 */

import type { Project } from "@studio/engine";
import { useCallback, useEffect, useState } from "react";
import { Account } from "./Account";
import { Connect } from "./Connect";
import { api, type User } from "./api";
import { Editor } from "./editor/Editor";
import { AcceptInvite, Forgot, Login, Reset } from "./Login";
import { Projects } from "./Projects";
import { LibraryPage } from "./SharedLibrary";
import { VoiceoverPage } from "./Voiceover";
import { VoicesPage } from "./Voices";

type Route =
  | { page: "projects" }
  | { page: "project"; id: string }
  | { page: "account" }
  | { page: "library" }
  | { page: "voiceover" }
  | { page: "voices" }
  | { page: "connect"; rid: string }
  | { page: "forgot" }
  | { page: "invite" | "reset"; token: string };

function route(): Route {
  const h = location.hash;
  let m;
  if ((m = h.match(/^#\/p\/([a-z0-9-]+)/))) return { page: "project", id: m[1] };
  if ((m = h.match(/^#\/(invite|reset)\/([\w-]+)/))) return { page: m[1] as "invite" | "reset", token: m[2] };
  if ((m = h.match(/^#\/connect\/([\w-]+)/))) return { page: "connect", rid: m[1] };
  if (h.startsWith("#/forgot")) return { page: "forgot" };
  if (h.startsWith("#/account")) return { page: "account" };
  if (h.startsWith("#/library")) return { page: "library" };
  if (h.startsWith("#/voiceover")) return { page: "voiceover" };
  if (h.startsWith("#/voices")) return { page: "voices" };
  return { page: "projects" };
}

const go = (hash: string) => {
  location.hash = hash;
};

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [r, setRoute] = useState(route());
  const [project, setProject] = useState<Project | null>(null);
  const [err, setErr] = useState("");
  const pid = r.page === "project" ? r.id : null;

  useEffect(() => {
    api.session().then((s) => setUser(s.user)).catch(() => setUser(null));
    const out = () => setUser(null);
    const hash = () => setRoute(route());
    window.addEventListener("studio:logged-out", out);
    window.addEventListener("hashchange", hash);
    return () => {
      window.removeEventListener("studio:logged-out", out);
      window.removeEventListener("hashchange", hash);
    };
  }, []);

  useEffect(() => {
    setProject(null);
    setErr("");
    if (pid && user) api.project(pid).then(setProject).catch((e) => setErr(e.message));
  }, [pid, user?.id]);

  const open = useCallback((id: string | null) => go(id ? `#/p/${id}` : "#/"), []);
  const signedIn = (u: User) => {
    setUser(u);
    if (r.page !== "project" && r.page !== "connect") go("#/");
  };

  if (user === undefined) return null;
  // emailed links work whether or not someone is signed in on this browser
  if (r.page === "invite") return <AcceptInvite token={r.token} onDone={signedIn} />;
  if (r.page === "reset") return <Reset token={r.token} onDone={() => api.session().then((s) => s.user && signedIn(s.user))} />;
  if (!user) return r.page === "forgot" ? <Forgot /> : <Login onDone={signedIn} />;
  if (r.page === "connect") return <Connect rid={r.rid} user={user} />;
  if (r.page === "account") return <Account user={user} onUser={setUser} />;
  if (r.page === "library") return <LibraryPage user={user} />;
  if (r.page === "voiceover") return <VoiceoverPage />;
  if (r.page === "voices") return <VoicesPage admin={user.admin} me={user.id} />;
  if (!pid) return <Projects user={user} onOpen={open} onLogout={() => api.logout().then(() => setUser(null))} />;
  if (err)
    return (
      <div className="center-msg">
        <p>Couldn't open the project: {err}</p>
        <button onClick={() => open(null)}>All projects</button>
      </div>
    );
  if (!project) return <div className="center-msg muted">Loading…</div>;
  return <Editor key={project.id} project={project} onExit={() => open(null)} />;
}
